import { useState, useEffect } from 'react';
import {
  getCaptchaImage,
  sendCaptchaSolution,
  sendStartLogin,
  getCheckAuthStatus,
  getCloseBrowser,
  cleanCaptchaSolutions,
} from '../services/invoice-downloader-api';
import { waitNSeconds } from '../utils/common-functions';
import { showErrorMessage, showSuccessMessage } from '../utils/toast-functions';
import { InvoiceDownloadResponse } from '../models/invoices/InvoiceDownloadApiResponse';

export type AuthStatusState = 'pending' | 'succeeded' | 'failed';
export type AuthStatusDetail =
  | 'not_initialized'
  | 'waiting_for_captcha_solution'
  | 'wrong_captcha'
  | 'bad_credentials'
  | 'too_many_retries'
  | 'blocked_access'
  | 'captcha_timeout'
  | 'unexpected_error';

export type AuthStatus = {
  status: AuthStatusState;
  detail: AuthStatusDetail;
  error: string;
};

const MAX_AUTH_STATUS_CHECK_ATTEMPTS = 40;
const AUTH_STATUS_CHECK_WAIT_TIME_SECONDS = 1;

export const useInvoiceDownloaderAuth = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');
  const [captchaImageBase64, setCaptchaImageBase64] = useState<string>('');
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [authReady, setAuthReady] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [captchaCode, setCaptchaCode] = useState<string>('');

  // Call cleanSolutions endpoint on hook initialization
  useEffect(() => {
    const callCleanSolutions = async () => {
      try {
        await cleanCaptchaSolutions();
        console.log('cleanSolutions called successfully');
      } catch (error) {
        console.error('Failed to call cleanSolutions:', error);
      }
    };

    callCleanSolutions();
  }, []);

  // Focus captcha input when showCaptcha becomes true
  useEffect(() => {
    if (showCaptcha) {
      setTimeout(() => {
        const captchaInput = document.querySelector(
          '[data-testid="captcha-code-input"]',
        ) as HTMLInputElement;
        if (captchaInput) {
          captchaInput.focus();
        }
      }, 100);
    }
  }, [showCaptcha]);

  const isFinishCondition = (authStatus: AuthStatus) => {
    const isFinish =
      authStatus.status === 'succeeded' ||
      authStatus.status === 'failed' ||
      (authStatus.status === 'pending' &&
        authStatus.detail === 'waiting_for_captcha_solution');

    if (isFinish) {
      if (authStatus.status === 'succeeded') {
        console.debug('Auth status check returned succeeded condition');
      } else if (authStatus.status === 'pending') {
        console.debug('Auth status check returned captcha required condition');
      } else {
        console.debug(
          'Auth status check returned failed condition: ' + authStatus.detail,
        );
      }
    }
    return isFinish;
  };

  const retryCheckStatus = async (
    max_attempts: number,
    sessionId: string,
  ): Promise<AuthStatus> => {
    let response: InvoiceDownloadResponse | undefined = undefined;
    let attempts = 0;
    while (attempts < max_attempts) {
      console.debug(`Auth status check - Waiting before next attempt...`);
      await waitNSeconds(AUTH_STATUS_CHECK_WAIT_TIME_SECONDS);
      console.debug(
        `Auth status check - Attempt number ${attempts + 1} of ${max_attempts}.`,
      );
      response = await getCheckAuthStatus(sessionId);
      attempts++;
      if (response?.success) {
        const authStatus = response.data as AuthStatus;
        if (isFinishCondition(authStatus)) {
          return authStatus;
        } else {
          console.debug(
            'Auth status check returned pending condition ' + authStatus.detail,
          );
        }
      } else {
        return {
          status: 'failed',
          detail: 'unexpected_error',
          error: 'Auth status check returned non-success response.',
        };
      }
    }
    console.debug('Auth status check failed. Giving up.');
    return {
      status: 'failed',
      detail: 'unexpected_error',
      error: 'Auth status check failed after maximum attempts. Giving up.',
    };
  };

  const handleAuthStatus = async (
    authStatus: AuthStatus,
    sessionId: string,
  ) => {
    if (authStatus.status === 'succeeded') {
      setAuthReady(true);
    } else if (
      authStatus.status === 'pending' &&
      authStatus.detail === 'waiting_for_captcha_solution'
    ) {
      const captchaImage = await getCaptchaImage(sessionId);
      if (captchaImage.success) {
        setCaptchaImageBase64(captchaImage.data as string);
        setShowCaptcha(true);
      } else {
        await loginFailed('Failed to get captcha image. Try again.', sessionId);
      }
    } else if (authStatus.status === 'failed') {
      if (authStatus.detail === 'bad_credentials') {
        await loginFailed('Wrong credentials. Please try again.', sessionId);
      } else if (authStatus.detail === 'too_many_retries') {
        await loginFailed(
          'Too many retries. Please try again later.',
          sessionId,
        );
      } else if (authStatus.detail === 'blocked_access') {
        await loginFailed('Blocked access. Please try again later.', sessionId);
      } else if (authStatus.detail === 'wrong_captcha') {
        await loginFailed(
          'Wrong captcha solution. Please try again.',
          sessionId,
        );
      } else if (authStatus.detail === 'captcha_timeout') {
        await loginFailed('Captcha timeout. Please try again.', sessionId);
      } else if (authStatus.detail === 'unexpected_error') {
        await loginFailed(
          'Unexpected error logging in: ' + authStatus.error,
          sessionId,
        );
      } else {
        await loginFailed(
          'Unknown error while logging in. Try again.',
          sessionId,
        );
      }
    }
  };

  const loginFailed = async (errorMessage: string, sessionId: string) => {
    await resetAuth(false, sessionId);
    setError(errorMessage);
  };

  const resetAuth = async (
    success: boolean,
    sessionIdToUse: string,
    skipCloseBrowser = false,
  ) => {
    if (!success && sessionIdToUse && !skipCloseBrowser) {
      const browserResponse = await getCloseBrowser(sessionIdToUse);
      console.log('Closing browser session:');
      console.log(browserResponse.data);
    }
    setShowCaptcha(false);
    setCaptchaImageBase64('');
    setCaptchaCode('');
    setAuthReady(false);
    setSessionId('');
  };

  const waitForLoginChanges = async (sessionId: string) => {
    console.debug('Waiting for login changes...');
    const authStatus: AuthStatus = await retryCheckStatus(
      MAX_AUTH_STATUS_CHECK_ATTEMPTS,
      sessionId,
    );
    await handleAuthStatus(authStatus, sessionId);
    setLoading(false);
  };

  const handleStartLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const sessionId = await sendStartLogin(username, password);
      console.log('Session ID: ');
      console.log(sessionId);
      setSessionId(sessionId);
      await waitForLoginChanges(sessionId);
    } catch (err) {
      setError('Failed to login');
      console.error(err);
      setLoading(false);
    }
  };

  const sendCaptcha = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    console.log('Sending captcha value: ', captchaCode);
    try {
      const success = await sendCaptchaSolution(sessionId, captchaCode);
      if (success) {
        showSuccessMessage('Captcha sent successfully');
        setShowCaptcha(false);
        setCaptchaCode('');
        console.debug(
          'Waiting for login to complete after captcha submission...',
        );

        await waitNSeconds(2);

        const authStatus = await retryCheckStatus(
          MAX_AUTH_STATUS_CHECK_ATTEMPTS,
          sessionId,
        );
        await handleAuthStatus(authStatus, sessionId);
      } else {
        showErrorMessage(
          'Captcha solution was incorrect or there was an error. Please try again.',
        );
        setError(
          'Captcha solution was incorrect or there was an error. Please try again.',
        );
        setCaptchaCode('');

        console.debug('Fetching new captcha image after incorrect solution...');
        const newCaptchaImage = await getCaptchaImage(sessionId);
        if (newCaptchaImage.success) {
          setCaptchaImageBase64(newCaptchaImage.data as string);
          setShowCaptcha(true);
        } else {
          await loginFailed(
            'Failed to get new captcha image. Please try again from the beginning.',
            sessionId,
          );
        }
      }
    } catch (err) {
      console.error('Error verifying captcha:', err);
      setError('Failed to verify captcha');
      showErrorMessage('Failed to verify captcha');
      setCaptchaCode('');

      try {
        console.debug('Fetching new captcha image after error...');
        const newCaptchaImage = await getCaptchaImage(sessionId);
        if (newCaptchaImage.success) {
          setCaptchaImageBase64(newCaptchaImage.data as string);
          setShowCaptcha(true);
        } else {
          await loginFailed(
            'Failed to get new captcha image. Please try again from the beginning.',
            sessionId,
          );
        }
      } catch (captchaErr) {
        console.error('Error fetching new captcha image:', captchaErr);
        await loginFailed(
          'Failed to verify captcha and get new image. Please try again from the beginning.',
          sessionId,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    username,
    password,
    sessionId,
    captchaImageBase64,
    showCaptcha,
    authReady,
    error,
    loading,
    captchaCode,
    // Setters
    setUsername,
    setPassword,
    setSessionId,
    setCaptchaCode,
    setError,
    setAuthReady,
    setCaptchaImageBase64,
    // Handlers
    handleStartLogin,
    sendCaptcha,
    resetAuth,
  };
};
